import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es'; // Import Spanish locale for dayjs
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Box, useMediaQuery, styled, Typography, Avatar } from '@mui/material';
import Badge from '@mui/material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HttpsIcon from '@mui/icons-material/Https';

import { message } from 'antd';

import LoadingInformation from '../../Components/LoadingInformation';

dayjs.locale('es');
const localizer = dayjsLocalizer(dayjs);

const StyledContainer = styled('div')(({ theme, isMobile }) => ({
  width: isMobile ? '100%' : '90%',
  margin: isMobile ? 0 : '2rem auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: isMobile ? 0 : '16px',
  boxShadow: theme.shadows[3],
  padding: isMobile ? theme.spacing(2) : theme.spacing(3),
  transition: 'all 0.3s ease',
  minHeight: '90vh',
  overflow: 'auto'
}));

const BirthdayWrapper = styled('div')(({ theme }) => ({
  backgroundColor: '#FFD1DC',
  color: theme.palette.common.white,
  borderRadius: '8px',
  padding: theme.spacing(1),
  margin: '2px',
  fontSize: '0.875rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  transition: 'transform 0.2s, box-shadow 0.2s',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const AgendaBirthdayWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
  margin: theme.spacing(1, 0),
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.common.white,
  },
}));

const BirthdayIconWrapper = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: '50%',
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: theme.shadows[1],
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  backgroundColor: '#FFB6C1',
  borderRadius: '8px',
  color: theme.palette.primary.contrastText,
}));

const CalendarStyles = styled('div')({
  '.rbc-date-cell': {
    position: 'relative',
  },
  '.has-birthdays': {
    position: 'relative',
  },
  '.rbc-month-row, .rbc-month-view, .rbc-row-content': {
    overflow: 'visible !important', // Permite que el badge sea visible
  },
  // Este es importante para asegurar que el wrapper tenga toda la celda
  '.rbc-day-bg': {
    position: 'relative',
  }
});

const messages = {
  next: "Siguiente",
  previous: "Anterior",
  today: "Hoy",
  month: "Mes",
  week: "Semana",
  day: "Día",
  date: "Fecha",
  time: "Hora",
  event: "Evento",
  allDay: "Todo el día",
};

function BirthdaysCalendar() {
  const [birthdays, setBirthdays] = useState([]);
  const [closestBirthdays, setClosestBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticationError, setAuthenticationError] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const isMobile = useMediaQuery('(max-width:768px)');
  const navigate = useNavigate();

  const birthdayStyleGetter = (event) => ({
    style: {
      backgroundColor: '#FF69B4',
      borderRadius: '8px',
      color: 'white',
      border: 'none',
      fontSize: isMobile ? '0.75rem' : '0.875rem',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  });

  const listBirthdays = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthenticationError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/birthdays', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error("No se pudieron cargar los cumpleaños");
      }

      const data = await response.json();

      const formattedBirthdays = data.map((birthday) => ({
        start: dayjs(birthday.birthday).toDate(),
        end: dayjs(birthday.birthday).toDate(),
        title: birthday.XVName,
        description: `Cumpleaños de ${birthday.XVName}` || 'Happy Birthday!',
        XVID: birthday.XVID
      }));

      setBirthdays(formattedBirthdays);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const listClosestBirthdays = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthenticationError(true);
      return messageApi.error({
        content: 'No se encontró el token de autenticación. Por favor, vuelve a iniciar sesión.',
        duration: 5
      }).then(() => {
        navigate('/sign-in');
      });
    }
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/birthdays/closest', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error("No se pudieron cargar los cumpleaños más cercanos");
      }

      const data = await response.json();

      const formattedBirthdays = data.map((birthday) => ({
        start: dayjs(birthday.birthday).toDate(),
        end: dayjs(birthday.birthday).toDate(),
        title: birthday.XVName,
        description: `Cumpleaños de ${birthday.XVName}` || 'Happy Birthday!',
      }));

      setClosestBirthdays(formattedBirthdays);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const dayPropGetter = (date) => {
    const dayBirthdays = birthdays.filter(
      (birthday) =>
        dayjs(birthday.start).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD')
    );

    if (dayBirthdays.length > 0) {
      return {
        className: dayBirthdays.length > 0 ? 'has-birthdays' : '',
        style: {
          position: 'relative',
          backgroundColor: dayBirthdays.length > 0 ? 'rgba(255, 209, 220, 0.2)' : undefined,
        },
      };
    }

    return {};
  };

  useEffect(() => {
    listBirthdays();
    listClosestBirthdays();
  }, []);

  const BirthdayComponent = ({ event }) => (
    <BirthdayWrapper onClick={() => navigate(`/events/birthday/${event.XVID}`)}>
      <BirthdayIconWrapper>
        <CakeIcon fontSize="small" color="primary" />
      </BirthdayIconWrapper>
      <div>
        <strong>{event.title}</strong>
        {!isMobile && <div>{event.description}</div>}
      </div>
    </BirthdayWrapper>
  );

  const AgendaBirthday = ({ event }) => (
    <AgendaBirthdayWrapper onClick={() => navigate(`/events/birthday/${event.XVID}`)}>
      <BirthdayIconWrapper>
        <CakeIcon fontSize="small" color="primary" />
      </BirthdayIconWrapper>
      <div>
        <strong style={{ color: '#E91E63', fontSize: '1rem' }}>{event.title}</strong>
        <p style={{ margin: '8px 0', color: '#666' }}>{event.description}</p>
        <small style={{ color: '#999' }}>
          {dayjs(event.start).format('MMMM D')}
        </small>
      </div>
    </AgendaBirthdayWrapper>
  );

  if (loading) {
    return <LoadingInformation />;
  }

  if (authenticationError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios registrados.
          </Typography>
        </Box>
      </Container>
    );
  };

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
        color="error.main"
      >
        <ErrorOutlineIcon fontSize="large" />
        <Typography variant="h6" mt={2}>
          Error al cargar los cumpleaños
        </Typography>
        <Typography variant="body2" mt={1} color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <StyledContainer isMobile={isMobile}>
      <HeaderContainer>
        <CakeIcon fontSize="large" />
        <Typography variant="h4" component="h1">
          Calendario de Cumpleaños
          <Typography variant="subtitle1" component="span" sx={{ ml: 2, opacity: 0.9 }}>
            ({closestBirthdays.length} cumpleaños próximos)
          </Typography>
        </Typography>
      </HeaderContainer>
      <CalendarStyles>
        <Calendar
          localizer={localizer}
          events={birthdays}
          views={['month', 'week', 'day', 'agenda']}
          defaultView={isMobile ? 'agenda' : 'month'}
          min={new Date(0, 0, 0, 8)}
          max={new Date(0, 0, 0, 23)}
          eventPropGetter={birthdayStyleGetter}
          messages={messages}
          dayPropGetter={dayPropGetter}
          components={{
            event: BirthdayComponent,
            agenda: { event: AgendaBirthday },
          }}
          style={{
            height: isMobile ? 'calc(100vh - 120px)' : '75vh',
            fontFamily: 'Roboto, sans-serif',
          }}
          componentsProps={{
            toolbar: {
              style: {
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                padding: '8px',
                marginBottom: '16px',
              },
            },
          }}
        />
      </CalendarStyles>
    </StyledContainer>
  );
}

export default BirthdaysCalendar;